import { useState } from 'react';

const faqs = [
  {
    q: 'What features does AdForge\'s AI ad generator include?',
    a: 'AdForge includes AI ad copywriting, AI-generated creative images, a built-in visual editor, multi-platform formatting, up to 20 A/B test variations, business profile memory, ad history library, and one-click export. Every feature is designed for small business owners with no marketing experience.',
  },
  {
    q: 'Does AdForge generate ad images or just text?',
    a: 'Both. AdForge is one of the few AI ad generators that produces complete ad creatives — AI-written copy and AI-generated images together in one workflow. The Free plan includes 1 image per generation, Pro includes 3, and Agency includes 6. You can also regenerate individual images without re-generating the copy.',
  },
  {
    q: 'Can I edit the AI-generated ads after they\'re created?',
    a: 'Yes — AdForge includes a full visual editor. Once your AI ad is generated, open it in the editor to drag and reposition elements, edit text inline, swap images, change colors, and fine-tune every detail. Export at any resolution when you\'re done. No design experience or external tools needed.',
  },
  {
    q: 'What platforms does AdForge create ads for?',
    a: 'AdForge generates ads for Facebook, Instagram, Google Ads, TikTok, and LinkedIn. Each ad is auto-formatted to match the platform\'s requirements — correct image ratios, character limits, and tone conventions — so your ads look native on every channel.',
  },
  {
    q: 'How many ad variations can I generate at once?',
    a: 'Up to 20 ad variations per generation. Each variation uses a different hook, angle, or messaging approach — not just minor rewrites. This makes A/B testing fast and data-driven. Higher-tier plans allow more generations per month.',
  },
  {
    q: 'Can I save and manage my ad library in AdForge?',
    a: 'Yes. Every ad you generate is automatically saved to your Ad History. You can browse, copy, edit, and regenerate any past ad at any time. For agencies, each client\'s ads are stored under their Business Profile — keeping everything organized.',
  },
];

export default function FeaturesFAQ() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section id="faq" className="py-20 lg:py-28 bg-stone-50">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-3 block">FAQ</span>
          <h2 className="text-3xl lg:text-4xl font-black text-stone-900 mb-4 leading-tight">
            Common Questions About AdForge Features
          </h2>
          <p className="text-stone-500 text-base">
            Everything you need to know about how the AI ad generator features work — and what to expect.
          </p>
        </div>

        <div
          itemScope
          itemType="https://schema.org/FAQPage"
          className="space-y-3"
        >
          {faqs.map((faq, idx) => (
            <article
              key={idx}
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
              className="rounded-xl border border-stone-100 bg-white overflow-hidden"
            >
              <button
                className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left cursor-pointer hover:bg-stone-50 transition-colors duration-200"
                onClick={() => setOpen(open === idx ? null : idx)}
                aria-expanded={open === idx}
              >
                <h3 itemProp="name" className="text-sm font-bold text-stone-900 leading-snug">
                  {faq.q}
                </h3>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className={`ri-${open === idx ? 'subtract' : 'add'}-line text-stone-400 text-base`} />
                </div>
              </button>

              {open === idx && (
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-5">
                  <p itemProp="text" className="text-sm text-stone-500 leading-relaxed">{faq.a}</p>
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
